import _ from "lodash";
import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { IconButton } from "@toddle-edu/ds-web";
import { AddOutlined } from "@toddle-edu/ds-icons";
import { Detector } from "react-detect-offline";

import { getUserType } from "modules/CommonSelectors";
import { compose, graphql } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import { useHandleScroll } from "Utils/CustomHooks";
import { prepareLoadMoreFeed, getGlobalTimezone } from "Utils";
import { StaffPermission } from "Utils/UserConditional";
import { useI18n } from "Hooks";

import classes from "./ChannelList.scss";
import ChannelTypeFilter from "./ChannelTypeFilter";
import Thread from "./Thread/Thread";
import { getChannelListQuery } from "./Utils/ThreadListQueries";
import { getChannelListFromCache } from "./Utils/ThreadListGraphqlHelpers";
import {
  getChannelListQueryVariables,
  isCreateAndManageOwnChannelEnabled,
  USER_SUPPORTED_CHANNELS,
  isMessagingRevempEnabled,
  getMessageTimeSinceText,
  CREATE_CHANNEL_CATEGORY,
} from "MessagingV2/modules/Utils";
import CreateChannelModal from "MessagingV2/components/Channels/CreateChannelModal";
import CreateFamilyChannelModal from "MessagingV2/components/Channels/CreateFamilyChannelModal";
import EmptyChannelList, {
  EMPTY_CHANNEL_LIST_VARIANTS,
} from "./EmptyChannelList";
import HeaderV2 from "../HeaderV2";

const areAllFiltersSelected = ({ selectedChannelTypes, userType }) => {
  if (_.isEmpty(selectedChannelTypes)) return true;
  const selectedChannelsSet = new Set(selectedChannelTypes);

  return _.every(USER_SUPPORTED_CHANNELS[userType], channelType =>
    selectedChannelsSet.has(channelType)
  );
};

const ChannelList = props => {
  const {
    chatId,
    onThreadClick,
    channelThreads,
    hasNextPage,
    loadMoreEvents,
    networkStatus,
    refetchChannelList,
    loggedInUserType,
    selectedChannelTypes,
    searchText,
  } = props;
  const { t } = useI18n(["common"]);

  const containerRef = useRef();

  useHandleScroll({
    networkStatus,
    loadMoreFeed: loadMoreEvents,
    hasNextPage: hasNextPage,
    getScrollRef: () => containerRef.current,
    isScrollAtBottom: true,
  });

  const handleNetworkChange = isOnline => {
    if (isOnline && _.isFunction(refetchChannelList)) refetchChannelList();
  };

  // if all filters selected, we treat it as no specific filter is selected
  const isNoFilterState = areAllFiltersSelected({
    selectedChannelTypes,
    userType: loggedInUserType,
  });

  return (
    <>
      <Detector
        polling={false}
        render={() => null}
        onChange={handleNetworkChange}
      />
      <div className={classes.channelMessagesContainer} ref={containerRef}>
        {_.size(channelThreads) ? (
          _.map(
            channelThreads,
            ({
              id,
              unreadMessageCount,
              totalUsersCount,
              label,
              profileImageData,
              isActive,
              isMuted,
              isArchived,
              type,
              lastActiveAt,
              createdAt,
            }) => {
              return (
                <Thread
                  id={id}
                  key={id}
                  avatarId={id}
                  profileImage={profileImageData?.url}
                  profileIcon={profileImageData?.icon}
                  isMainTextHighlighted={unreadMessageCount > 0 && !isMuted}
                  mainText={label}
                  subText={t("common:count_members", {
                    count: totalUsersCount,
                  })}
                  unreadMessageCount={unreadMessageCount}
                  timeSinceLastMessage={getMessageTimeSinceText(
                    lastActiveAt || createdAt,
                    t
                  )}
                  isChatMuted={isMuted}
                  chatType={type}
                  selected={chatId == id}
                  onlineStatus={false}
                  isArchived={isArchived}
                  onThreadClick={() => onThreadClick(id)}
                />
              );
            }
          )
        ) : (
          <EmptyChannelList
            variant={
              searchText
                ? EMPTY_CHANNEL_LIST_VARIANTS.SEARCH
                : isNoFilterState
                ? EMPTY_CHANNEL_LIST_VARIANTS.NO_FILTERS
                : EMPTY_CHANNEL_LIST_VARIANTS.APPLIED_FILTERS
            }
            searchText={searchText}
            channelFilters={selectedChannelTypes}
          />
        )}
        {networkStatus == 3 ? (
          <div className={classes.loadingContainer}>
            <Loading />
          </div>
        ) : null}
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  loggedInUserType: getUserType(state),
  currentTimezone: getGlobalTimezone(),
});

const ChannelListWithData = compose(
  connect(mapStateToProps),
  graphql(getChannelListQuery, {
    name: "getChannelList",
    options: ({ searchText, selectedChannelTypes }) => ({
      fetchPolicy: "cache-and-network",
      variables: getChannelListQueryVariables({
        channelTypes: selectedChannelTypes,
        searchText,
      }),
    }),
    props({ getChannelList }) {
      const { variables } = getChannelList;
      const chatResult = getChannelListFromCache({ variables });

      const pageInfo = _.get(chatResult, "node.chats.pageInfo", {});
      const hasNextPage = _.get(
        chatResult,
        "node.chats.pageInfo.hasNextPage",
        false
      );

      const channelThreads = _.map(
        _.get(chatResult, "node.chats.edges", []),
        chat => _.get(chat, "node")
      );

      return {
        isData: !_.isEmpty(chatResult),
        isLoading: _.includes([1, 2], getChannelList["networkStatus"]),
        channelThreads,
        hasNextPage,
        getChannelList,
        refetchChannelList: getChannelList.refetch,
        networkStatus: getChannelList.networkStatus,
        loadMoreEvents: prepareLoadMoreFeed({
          variables: {
            ...variables,
            input: {
              ...variables.input,
              after: _.get(pageInfo, "endCursor", null),
            },
          },
          queryData: getChannelList,
          key: "chats",
        }),
      };
    },
  }),
  withLoader
)(ChannelList);

const ChannelListWrapper = props => {
  const {
    updateChannelTypes,
    selectedChannelTypes,
    showChannelTypeFilter = true,
  } = props;

  const [createChannelType, setCreateChannelType] = useState(null);

  const canCreateChannel = isCreateAndManageOwnChannelEnabled();

  const onCloseChannelModal = () => {
    setCreateChannelType(null);
  };

  const onOpenChannelModal = channelType => {
    setCreateChannelType(channelType);
  };

  const [searchText, setSearchText] = useState("");

  return (
    <>
      {showChannelTypeFilter ? (
        isMessagingRevempEnabled() ? (
          <HeaderV2
            addNewChannel={onOpenChannelModal}
            canCreateChannel={canCreateChannel}
            setSearchText={setSearchText}
          />
        ) : (
          <div className={classes.filterHeaderContainer}>
            <ChannelTypeFilter
              value={selectedChannelTypes}
              onOptionsSelected={updateChannelTypes}
            />

            <StaffPermission>
              {canCreateChannel ? (
                <div className={classes.iconBtn}>
                  <IconButton
                    icon={<AddOutlined />}
                    size="x-small"
                    onClick={() =>
                      onOpenChannelModal(CREATE_CHANNEL_CATEGORY.CUSTOM_CHANNEL)
                    }
                    variant="progressive"
                  />
                </div>
              ) : null}
            </StaffPermission>
          </div>
        )
      ) : null}
      <ChannelListWithData
        {...props}
        {...(_.trim(searchText) && { searchText })}
      />
      {_.isEqual(createChannelType, CREATE_CHANNEL_CATEGORY.CUSTOM_CHANNEL) ? (
        <CreateChannelModal onCloseModal={onCloseChannelModal} />
      ) : null}
      {_.isEqual(createChannelType, CREATE_CHANNEL_CATEGORY.FAMILY_CHANNEL) ? (
        <CreateFamilyChannelModal onCloseModal={onCloseChannelModal} />
      ) : null}
    </>
  );
};

ChannelListWrapper.propTypes = {
  selectedChannelTypes: PropTypes.arrayOf(PropTypes.string),
  updateChannelTypes: PropTypes.func,
};

ChannelListWrapper.defaultProps = {
  selectedChannelTypes: [],
  updateChannelTypes: () => {},
};

export default ChannelListWrapper;
